import React from 'react'
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { User } from '../../../types/models/user'
import {
  UsersTableCard,
  PrimaryButton,
  DangerButton,
  OutlineButton,
  StyledTable,
  StatusBadge,
  HeaderActions,
} from './styles'

interface UsersTableProps {
  users: User[]
  selectedUsers: string[]
  sortConfig: {
    key: string
    direction: 'ascending' | 'descending' | null
  }
  onSort: (key: string) => void
  onToggleUserSelection: (userId: string) => void
  onToggleAllUsers: () => void
  onEditUser: (user: User) => void
  onDeleteUser: (user: User) => void
  onBulkDelete: () => void
  onCreateUser: () => void
  formatDate: (dateString: string) => string
  organizations: { id: string; name: string }[]
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  selectedUsers,
  sortConfig,
  onSort,
  onToggleUserSelection,
  onToggleAllUsers,
  onEditUser,
  onDeleteUser,
  onBulkDelete,
  onCreateUser,
  formatDate,
  organizations,
}) => {
  const getUserStatus = (user: User) => {
    const lastLogin = new Date(user.last_login || '')
    const createdAt = new Date(user.created_at)
    const now = new Date()

    // Check if user has never logged in (new user)
    if (lastLogin.getFullYear() < 2000) {
      const daysSinceCreation = Math.floor(
        (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24)
      )
      if (daysSinceCreation >= 30) {
        return { status: 'danger', message: 'Never logged in' }
      }
      return { status: 'new', message: 'New User' }
    }

    // Check if user is inactive (hasn't logged in for 14-30 days)
    const daysSinceLogin = Math.floor((now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60 * 24))
    if (daysSinceLogin >= 30) {
      return { status: 'danger', message: formatDate(user.last_login || '') }
    } else if (daysSinceLogin >= 14) {
      return { status: 'warning', message: formatDate(user.last_login || '') }
    }

    return { status: 'active', message: formatDate(user.last_login || '') }
  }

  const getSortedUsers = () => {
    if (!users) return []

    const regularUsers = [...users.filter((user) => user.username !== 'admin')]

    if (!sortConfig.direction) return regularUsers

    return regularUsers.sort((a, b) => {
      let aValue: string | number | Date
      let bValue: string | number | Date

      switch (sortConfig.key) {
        case 'name':
          aValue = `${a.first_name} ${a.last_name}`
          bValue = `${b.first_name} ${b.last_name}`
          break
        case 'email':
          aValue = a.email
          bValue = b.email
          break
        case 'username':
          aValue = a.username
          bValue = b.username
          break
        case 'created_at':
          aValue = new Date(a.created_at)
          bValue = new Date(b.created_at)
          break
        case 'last_login':
          aValue = new Date(a.last_login || '')
          bValue = new Date(b.last_login || '')
          break
        default:
          return 0
      }

      if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1
      return 0
    })
  }

  // Helper to map org_id to org name
  const orgIdToName = React.useMemo(() => {
    const map: Record<string, string> = {}
    organizations.forEach((org) => {
      map[org.id] = org.name
    })
    return map
  }, [organizations])

  return (
    <UsersTableCard>
      <div className="card-header">
        <h4 className="table-header-title">User Management</h4>
        <HeaderActions>
          {selectedUsers.length > 0 && (
            <DangerButton onClick={onBulkDelete}>
              <i className="fas fa-trash-alt me-2"></i>Delete Selected ({selectedUsers.length})
            </DangerButton>
          )}
          <PrimaryButton onClick={onCreateUser}>
            <i className="fas fa-plus me-2"></i>Create New User
          </PrimaryButton>
        </HeaderActions>
      </div>
      <div className="card-body">
        <div className="table-responsive">
          <StyledTable>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <Form.Check
                    type="checkbox"
                    onChange={onToggleAllUsers}
                    checked={
                      selectedUsers.length ===
                      (users.filter((user) => user.username !== 'admin').length || 0)
                    }
                  />
                </th>
                <th
                  onClick={() => onSort('name')}
                  style={{ cursor: 'pointer' }}
                  className="text-nowrap"
                >
                  Name{' '}
                  <i
                    className={`fas fa-sort${
                      sortConfig.key === 'name'
                        ? sortConfig.direction === 'ascending'
                          ? '-up'
                          : '-down'
                        : ''
                    } ms-1`}
                    style={{
                      color: sortConfig.key === 'name' ? '#22d3ee' : 'rgba(255, 255, 255, 0.5)',
                    }}
                  ></i>
                </th>
                <th
                  onClick={() => onSort('email')}
                  style={{ cursor: 'pointer' }}
                  className="text-nowrap"
                >
                  Email{' '}
                  <i
                    className={`fas fa-sort${
                      sortConfig.key === 'email'
                        ? sortConfig.direction === 'ascending'
                          ? '-up'
                          : '-down'
                        : ''
                    } ms-1`}
                    style={{
                      color: sortConfig.key === 'email' ? '#22d3ee' : 'rgba(255, 255, 255, 0.5)',
                    }}
                  ></i>
                </th>
                <th
                  onClick={() => onSort('username')}
                  style={{ cursor: 'pointer' }}
                  className="text-nowrap"
                >
                  Username{' '}
                  <i
                    className={`fas fa-sort${
                      sortConfig.key === 'username'
                        ? sortConfig.direction === 'ascending'
                          ? '-up'
                          : '-down'
                        : ''
                    } ms-1`}
                    style={{
                      color: sortConfig.key === 'username' ? '#22d3ee' : 'rgba(255, 255, 255, 0.5)',
                    }}
                  ></i>
                </th>
                <th>Role</th>
                <th>Organization</th>
                <th
                  onClick={() => onSort('created_at')}
                  style={{ cursor: 'pointer' }}
                  className="text-nowrap"
                >
                  Created At{' '}
                  <i
                    className={`fas fa-sort${
                      sortConfig.key === 'created_at'
                        ? sortConfig.direction === 'ascending'
                          ? '-up'
                          : '-down'
                        : ''
                    } ms-1`}
                    style={{
                      color:
                        sortConfig.key === 'created_at' ? '#22d3ee' : 'rgba(255, 255, 255, 0.5)',
                    }}
                  ></i>
                </th>
                <th
                  onClick={() => onSort('last_login')}
                  style={{ cursor: 'pointer' }}
                  className="text-nowrap"
                >
                  Last Login{' '}
                  <i
                    className={`fas fa-sort${
                      sortConfig.key === 'last_login'
                        ? sortConfig.direction === 'ascending'
                          ? '-up'
                          : '-down'
                        : ''
                    } ms-1`}
                    style={{
                      color:
                        sortConfig.key === 'last_login' ? '#22d3ee' : 'rgba(255, 255, 255, 0.5)',
                    }}
                  ></i>
                </th>
                <th style={{ width: '200px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getSortedUsers().map((user) => {
                const status = getUserStatus(user)
                return (
                  <tr key={user.id}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => onToggleUserSelection(user.id)}
                      />
                    </td>
                    <td>{`${user.first_name} ${user.last_name}`}</td>
                    <td>{user.email}</td>
                    <td>{user.username}</td>
                    <td>
                      <StatusBadge $variant="info">User</StatusBadge>
                    </td>
                    <td>
                      {orgIdToName[user.org_id] || (
                        <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>N/A</span>
                      )}
                    </td>
                    <td>{formatDate(user.created_at)}</td>
                    <td>
                      {(() => {
                        if (status.status === 'new') {
                          return (
                            <OverlayTrigger
                              placement="right"
                              overlay={
                                <Tooltip id={`tooltip-${user.id}`}>
                                  Created: {formatDate(user.created_at)}
                                </Tooltip>
                              }
                            >
                              <div style={{ cursor: 'help', display: 'inline-block' }}>
                                <StatusBadge $variant="new">
                                  <i className="fas fa-star me-1"></i>
                                  New User
                                </StatusBadge>
                              </div>
                            </OverlayTrigger>
                          )
                        } else if (status.status === 'warning') {
                          return (
                            <OverlayTrigger
                              placement="right"
                              overlay={
                                <Tooltip id={`tooltip-${user.id}`}>
                                  Last login: {status.message}
                                </Tooltip>
                              }
                            >
                              <div style={{ cursor: 'help', display: 'inline-block' }}>
                                <StatusBadge $variant="warning">
                                  <i className="fas fa-clock me-1"></i>
                                  Inactive
                                </StatusBadge>
                              </div>
                            </OverlayTrigger>
                          )
                        } else if (status.status === 'danger') {
                          return (
                            <OverlayTrigger
                              placement="right"
                              overlay={
                                <Tooltip id={`tooltip-${user.id}`}>{status.message}</Tooltip>
                              }
                            >
                              <div style={{ cursor: 'help', display: 'inline-block' }}>
                                <StatusBadge $variant="danger">
                                  <i className="fas fa-exclamation-circle me-1"></i>
                                  Inactive
                                </StatusBadge>
                              </div>
                            </OverlayTrigger>
                          )
                        } else {
                          return <span>{status.message}</span>
                        }
                      })()}
                    </td>
                    <td>
                      <div className="d-flex gap-2 justify-content-center">
                        <OutlineButton
                          size="sm"
                          onClick={() => onEditUser(user)}
                          style={{ width: '80px' }}
                        >
                          Edit
                        </OutlineButton>
                        <OutlineButton
                          size="sm"
                          onClick={() => onDeleteUser(user)}
                          style={{
                            width: '80px',
                            background: 'rgba(239, 68, 68, 0.1)',
                            borderColor: 'rgba(239, 68, 68, 0.3)',
                            color: '#ef4444',
                          }}
                        >
                          Delete
                        </OutlineButton>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </StyledTable>
        </div>
      </div>
    </UsersTableCard>
  )
}

export default UsersTable
